import { useState, useMemo, useEffect } from "react";
import PropTypes from "prop-types";

import { colors, strCode } from "../../../consts";
import { t } from "../../../services";
import { arrayUtil, dropdownUtil } from "../../../utils";

import {
  DropdownInput,
  FlexContainers,
  Modal,
  SmallButton,
  TextButton,
  WrapperContainer
} from "../../../components";

const LocationHardwareModal = ({
  locationHardware,
  hardwares,
  locations,
  isMercury,
  loading,
  active,
  setActive,
  onSave
}) => {
  const [selectedHardwares, setSelectedHardwares] = useState([]);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const locationName = useMemo(() => {
    return (
      locations.find((location) => location.id === locationHardware?.locationId)?.locationName ??
      locationHardware?.locationId
    );
  }, [locationHardware, locations]);

  const hardwareOptions = useMemo(() => {
    return hardwares.map((el) => {
      return { id: el, value: isMercury ? el?.slice(0, 17) : el?.slice(-8) };
    });
  }, [hardwares]);

  const isSameData = useMemo(() => {
    const { hardware = [] } = locationHardware ?? {};

    return arrayUtil.arrayEquals(
      hardware,
      selectedHardwares?.map((el) => el?.id)
    );
  }, [selectedHardwares, locationHardware]);

  useEffect(() => {
    if (locationHardware && hardwareOptions && !isSubmitted) {
      const { hardware } = locationHardware;

      setSelectedHardwares(hardwareOptions.filter((el) => hardware.includes(el.id)));
    }
  }, [locationHardware, hardwareOptions, isSubmitted]);

  useEffect(() => {
    if (isSubmitted && !loading && active) {
      onClose();
    }
  }, [isSubmitted, loading, active]);

  const handleSave = async () => {
    setIsSubmitted(true);
    await onSave(selectedHardwares);
  };

  const onClose = () => {
    setActive(false);
    setIsSubmitted(false);
    setSelectedHardwares([]);
  };

  return (
    <>
      <Modal
        id={"location-hardware-modal"}
        active={active}
        closable={false}
        title={locationName}
        preventOverflow>
        <WrapperContainer Container={FlexContainers.FlexColumnStart} gap={24} width={600}>
          <DropdownInput
            id="location-hardware-selector"
            title={t(strCode.SELECT_LOCATION_HARDWARE_TITLE)}
            placeholder={t(
              isMercury
                ? strCode.HARDWARE_SSIDS_PLACEHOLDER
                : strCode.HARDWARE_IDS_PLACEHOLDER
            )}
            options={hardwareOptions}
            selectedOptions={selectedHardwares}
            onSelectOption={(id) => {
              setSelectedHardwares(
                dropdownUtil.updateSelectedOptions(
                  id,
                  selectedHardwares,
                  hardwareOptions,
                  true,
                  true
                )
              );
            }}
            isSearchable
            useChips
          />
          <WrapperContainer
            Container={
              selectedHardwares?.length ? FlexContainers.FlexRowBetween : FlexContainers.FlexRowEnd
            }
            gap={8}
            width={"100%"}>
            {!!selectedHardwares?.length && (
              <TextButton
                color={colors.ERROR}
                onClick={() => setSelectedHardwares([])}
                disabled={loading}>
                {t(strCode.UNASSIGN_ALL_BTN)}
              </TextButton>
            )}

            <WrapperContainer Container={FlexContainers.FlexRowStart} gap={8}>
              <SmallButton secondary onClick={onClose} disabled={loading}>
                {t(strCode.CANCEL_BTN)}
              </SmallButton>
              <SmallButton onClick={handleSave} disabled={isSameData || loading} loading={loading}>
                {t(strCode.SAVE_BTN)}
              </SmallButton>
            </WrapperContainer>
          </WrapperContainer>
        </WrapperContainer>
      </Modal>
    </>
  );
};

LocationHardwareModal.propTypes = {
  locationHardware: PropTypes.shape({
    locationId: PropTypes.string.isRequired,
    hardware: PropTypes.arrayOf(PropTypes.string).isRequired
  }),
  hardwares: PropTypes.arrayOf(PropTypes.string).isRequired,
  locations: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      locationName: PropTypes.string
    })
  ).isRequired,
  isMercury: PropTypes.bool.isRequired,
  loading: PropTypes.bool.isRequired,
  active: PropTypes.bool.isRequired,
  setActive: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired
};

export default LocationHardwareModal;
